import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AudioLines, Compass, HandHeart, Music2, MessageSquare, Sparkles, Shield, Clock, Users } from 'lucide-react';
import { faqApi } from '@/api';
import { Accordion, AccordionItem } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FeatureFlipCard } from '@/components/FeatureFlipCard';
import { DraggableMarquee } from '@/components/DraggableMarquee';
import { HeroCarousel } from '@/components/HeroCarousel';

// Файлы для back-стороны кладите в client/public/features/*.gif/.mp4 (или .png/.jpg).
// Если файла нет - media скроется через onError, останется красивый градиент.
const features = [
  { icon: Compass,        title: 'Новые механики', text: 'Костры дают регенерацию, подзорная труба показывает HP и расстояние до мобов, тотем спасает от пустоты, а светящиеся ягоды изменяют размер игрока.', media: '/features/survival.mp4' },
  { icon: MessageSquare,  title: 'Голосовой чат',  text: 'Полноценный proximity voice - обсуждайте, шутите и стройте вместе.', media: '/features/voice.mp4' },
  { icon: AudioLines,     title: 'Голосовые сообщения', text: 'Отправляйте голосовые сообщения прямо в игровой чат.', media: '/features/voicemes.mp4' },
  { icon: Music2,         title: 'Своя музыка',    text: 'Создавайте пластинки со своими треками и слушайте их в игре.', media: '/features/musicdisc.mp4' },
  { icon: Sparkles,       title: 'Эмоции',         text: 'Поддержка Emotecraft - выражайтесь без слов.', media: '/features/emotes.mp4' },
  { icon: HandHeart,      title: 'PatPat',         text: 'Гладьте игроков и любых живых мобов, создавая милую и дружелюбную атмосферу на сервере.', media: '/features/patpat.mp4' },
  { icon: Shield,         title: 'Безопасность',   text: 'Сохранность построек и вещей гарантирована.', media: '/features/protect.mp4' },
];

export function HomePage() {
  const { data: faq } = useQuery({ queryKey: ['faq'], queryFn: faqApi.list, staleTime: 60_000 });
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(120% 70% at 80% 10%, rgba(255,149,206,0.18), transparent 60%), radial-gradient(80% 60% at 10% 80%, rgba(139,30,63,0.25), transparent 60%), #1B1B1B',
          }}
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-eol-pink font-semibold mb-4 tracking-wider uppercase text-sm">
              Minecraft Survival
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-eol-white">
              Окунись в мир{' '}
              <span className="text-eol-pink">EOL</span>
            </h1>
            <p className="mt-6 text-lg text-eol-desc max-w-xl leading-relaxed">
              Сервер выживания нового поколения - с уникальными механиками, голосовым чатом
              и постоянными обновлениями. Присоединяйтесь и играйте с друзьями.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/shop">Начать играть</Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <a href="#features">Показать больше</a>
              </Button>
            </div>
          </div>
          <div className="hidden lg:flex justify-end items-center">
            <div className="w-full max-w-md">
              <HeroCarousel images={['/main1.webp', '/main2.webp', '/main3.webp']} />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-eol-white">
              Что из себя представляет наш проект?
            </h2>
            <p className="mt-4 text-eol-desc">
              <strong className="text-eol-white">Выживание</strong> — закрытый сервер,
              где гарантируются комфортная игра и сохранность ваших построек.{' '}
              <strong className="text-eol-white">Первый сезон</strong> — мир, который со временем расширяется сам.
              На сервере также доступны собственные новые кастомные механики, делающие выживание интереснее и разнообразнее.
            </p>
          </div>

          <div className="-mx-6 lg:-mx-12 py-4">
            <DraggableMarquee>
              {features.map((f) => (
                <FeatureFlipCard
                  key={f.title}
                  icon={f.icon}
                  title={f.title}
                  text={f.text}
                  media={f.media}
                />
              ))}
            </DraggableMarquee>
          </div>
        </div>
      </section>

      {/* Particularities */}
      <section className="py-20 bg-eol-black/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="pt-6">
              <Clock className="h-7 w-7 text-eol-pink mb-3" />
              <h3 className="font-bold text-eol-white mb-1">Круглосуточная работа</h3>
              <p className="text-sm text-eol-desc">Сервер 24/7 - играйте в любое время.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Sparkles className="h-7 w-7 text-eol-pink mb-3" />
              <h3 className="font-bold text-eol-white mb-1">Частые обновления</h3>
              <p className="text-sm text-eol-desc">Поддержка последней стабильной версии Minecraft.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Users className="h-7 w-7 text-eol-pink mb-3" />
              <h3 className="font-bold text-eol-white mb-1">Поддержка</h3>
              <p className="text-sm text-eol-desc">Мы на связи и решим любые вопросы.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-eol-white">
            Есть <span className="text-eol-pink">вопросы</span> или{' '}
            <span className="text-eol-pink">проблемы</span>? Пишите в Telegram!
          </h2>
          <Button asChild size="lg" className="mt-8">
            <a href="https://t.me/eol_support" target="_blank" rel="noreferrer">
              Написать в тех. поддержку
            </a>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-eol-black/30">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold text-eol-white text-center mb-12">
            Серверное <span className="text-eol-pink">FAQ</span>
          </h2>
          {!faq || faq.length === 0 ? (
            <p className="text-center text-eol-desc">Загрузка…</p>
          ) : (
            <Accordion>
              {faq.map((item) => (
                <AccordionItem key={item.id} title={item.question}>
                  {item.answer}
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>
    </>
  );
}
